import { useState } from 'react';
import { Alert, Button, Checkbox, Group, Loader, Select, Stack, Switch, Tabs } from '@mantine/core';
import { IconAlertTriangle, IconDownload, IconInfoCircle } from '@tabler/icons-react';
import BillingEditModal from 'src/secret-menu/billing/BillingEditModal';
import { formatBillingMonth, nextBillingMonth } from 'src/secret-menu/billing/billingUtils';
import type { BillingDetail } from 'src/secret-menu/billing/hooks/useBilling';
import { useBillingDetails } from 'src/secret-menu/billing/hooks/useBillingDetails';
import { useBillingExport } from 'src/secret-menu/billing/hooks/useBillingExport';
import { useUsageMetrics } from './hooks/useUsageMetrics';
import { useUsageMetricsExport } from './hooks/useUsageMetricsExport';
import FirmDetail from './overview/FirmDetail';
import FirmsPanel from './overview/FirmsPanel';
import { LENS_LABELS, LENS_ORDER, type Lens } from './overview/lenses';
import { buildRevenueModel } from './overview/revenue';
import UsageDashboard from './overview/UsageDashboard';
import { buildUsageModel } from './overview/usageModel';

export default function UsageMetricsTab() {
  const [includeInternal, setIncludeInternal] = useState(false);
  const [includeMessages, setIncludeMessages] = useState(false);
  const [includeConversations, setIncludeConversations] = useState(false);
  const [month, setMonth] = useState<string | null>(null);
  const [lens, setLens] = useState<Lens>('raw');
  const [selectedFirm, setSelectedFirm] = useState<string | null>(null);
  const [editBilling, setEditBilling] = useState<BillingDetail | null>(null);

  const {
    rows,
    isLoading: isUsageLoading,
    isError: isUsageError,
  } = useUsageMetrics(includeInternal);
  const { downloadExport, isLoading: isExporting } = useUsageMetricsExport();
  const { downloadExport: downloadBilling, isLoading: isExportingBilling } = useBillingExport();
  const billingMonthLabel = formatBillingMonth(nextBillingMonth());
  const {
    data: billingData,
    isLoading: isBillingLoading,
    isError: isBillingError,
  } = useBillingDetails();

  // Billing is joined onto every panel here, so a billing failure has to gate the tab alongside
  // usage - a missing payload is indistinguishable from "no billing configured" once rendered.
  const isLoading = isUsageLoading || isBillingLoading;
  const isError = isUsageError || isBillingError;
  const failedSources = [
    ...(isUsageError ? ['usage metrics'] : []),
    ...(isBillingError ? ['billing details'] : []),
  ].join(' and ');

  const today = new Date();
  const model = buildUsageModel(rows ?? [], today);
  const revenue = buildRevenueModel(
    billingData,
    model?.firms.map(entry => entry.name) ?? [],
    includeInternal,
  );

  const activeMonth =
    model && month && model.months.includes(month) ? month : (model?.reportingMonth ?? null);
  const firm =
    model && selectedFirm ? model.firms.find(entry => entry.name === selectedFirm) : undefined;

  return (
    <Stack gap="md">
      <Group justify="space-between" align="flex-start" wrap="wrap">
        <Group gap="lg" wrap="wrap">
          <Switch
            label="Include internal companies"
            checked={includeInternal}
            onChange={event => setIncludeInternal(event.currentTarget.checked)}
          />
          <Checkbox
            label="Include Siesta messages in totals"
            checked={includeMessages}
            onChange={event => setIncludeMessages(event.currentTarget.checked)}
          />
          <Checkbox
            label="Include conversations in totals"
            checked={includeConversations}
            onChange={event => setIncludeConversations(event.currentTarget.checked)}
          />
        </Group>

        <Stack gap="xs" align="flex-end">
          <Group gap="xs">
            <Button
              leftSection={<IconDownload size={16} />}
              variant="light"
              loading={isExporting}
              onClick={() =>
                downloadExport({ includeInternal, includeMessages, includeConversations })
              }
            >
              Download xlsx
            </Button>
            <Button
              leftSection={<IconDownload size={16} />}
              variant="light"
              loading={isExportingBilling}
              onClick={() => downloadBilling({ includeInternal })}
            >
              Download {billingMonthLabel} billing
            </Button>
          </Group>

          {model && activeMonth && (
            <Group gap="sm" align="flex-start" wrap="nowrap">
              <Select
                label="Month"
                size="xs"
                w={160}
                data={model.months
                  .map(key => ({ value: key, label: model.monthLabels.get(key) ?? key }))
                  .reverse()}
                value={activeMonth}
                onChange={setMonth}
                allowDeselect={false}
              />
              <Select
                label="Lens"
                size="xs"
                w={200}
                data={LENS_ORDER.map(value => ({ value, label: LENS_LABELS[value] }))}
                value={lens}
                onChange={value => setLens((value as Lens | null) ?? 'raw')}
                allowDeselect={false}
              />
            </Group>
          )}
        </Stack>
      </Group>

      {isLoading && (
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      )}

      {isError && (
        <Alert icon={<IconAlertTriangle />} color="red" variant="light">
          Could not load {failedSources}. Try refreshing the page.
        </Alert>
      )}

      {!isLoading && !isError && !model && (
        <Alert icon={<IconInfoCircle />} variant="light">
          No usage data yet.
        </Alert>
      )}

      {!isLoading && !isError && model && activeMonth && firm && (
        <FirmDetail
          model={model}
          firm={firm}
          month={activeMonth}
          lens={lens}
          today={today}
          revenue={revenue}
          onEditBilling={setEditBilling}
          onBack={() => setSelectedFirm(null)}
        />
      )}

      {!isLoading && !isError && model && activeMonth && !firm && (
        <Tabs defaultValue="overview" keepMounted={false}>
          <Tabs.List>
            <Tabs.Tab value="overview">Overview</Tabs.Tab>
            <Tabs.Tab value="firms">Client firms ({model.firms.length})</Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="md">
            <UsageDashboard
              model={model}
              revenue={revenue}
              month={activeMonth}
              lens={lens}
              today={today}
              onSelectFirm={setSelectedFirm}
            />
          </Tabs.Panel>
          <Tabs.Panel value="firms" pt="md">
            <FirmsPanel
              model={model}
              month={activeMonth}
              lens={lens}
              today={today}
              revenue={revenue}
              onSelectFirm={setSelectedFirm}
            />
          </Tabs.Panel>
        </Tabs>
      )}

      <BillingEditModal billingDetail={editBilling} onClose={() => setEditBilling(null)} />
    </Stack>
  );
}
